import { collection, getDocs, query, where, orderBy, limit, DocumentData, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';

// Types for the dashboard data
export interface DashboardStats {
  totalUsers: number;
  totalAhavexers: number;
  totalProducts: number;
  totalRevenue: number;
  recentActivities: RecentActivity[];
  systemHealth: SystemHealth;
}

export interface RecentActivity {
  type: 'user_registered' | 'ahavexer_onboarded' | 'order_completed';
  data: DocumentData;
  timestamp: Date;
}

export interface SystemHealth {
  serverUptime: number;
  databasePerformance: number;
  apiResponseTime: number;
  storageCapacity: number;
}

// Function to fetch all dashboard stats
export const fetchDashboardStats = async (): Promise<DashboardStats> => {
  try {
    const [
      totalUsers,
      totalAhavexers,
      totalProducts,
      totalRevenue,
      recentActivities,
      systemHealth
    ] = await Promise.all([
      fetchTotalUsers(),
      fetchTotalAhavexers(),
      fetchTotalProducts(),
      fetchTotalRevenue(),
      fetchRecentActivities(),
      fetchSystemHealth()
    ]);

    return {
      totalUsers,
      totalAhavexers,
      totalProducts,
      totalRevenue,
      recentActivities,
      systemHealth
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

// Function to fetch total number of users
const fetchTotalUsers = async (): Promise<number> => {
  try {
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error fetching total users:', error);
    return 0;
  }
};

// Function to fetch total number of ahavexers
const fetchTotalAhavexers = async (): Promise<number> => {
  try {
    const usersRef = collection(db, 'users');
    // Query for users that have ahavexId field
    const ahavexersQuery = query(usersRef, where('ahavexId', '!=', null));
    const querySnapshot = await getDocs(ahavexersQuery);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error fetching total ahavexers:', error);
    return 0;
  }
};

// Function to fetch total number of products
const fetchTotalProducts = async (): Promise<number> => {
  try {
    const productsRef = collection(db, 'products');
    const querySnapshot = await getDocs(productsRef);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error fetching total products:', error);
    return 0;
  }
};

// Function to fetch total revenue
const fetchTotalRevenue = async (): Promise<number> => {
  try {
    const ordersRef = collection(db, 'orders');
    const querySnapshot = await getDocs(ordersRef);
    
    let totalRevenue = 0;
    querySnapshot.forEach((doc) => {
      const orderData = doc.data();
      
      // Check if total field exists directly
      if (orderData.total && typeof orderData.total === 'number') {
        totalRevenue += orderData.total;
      } 
      // Otherwise calculate from items
      else if (orderData.items && Array.isArray(orderData.items)) {
        orderData.items.forEach((item: any) => {
          if (item && item.price && item.quantity) {
            totalRevenue += (Number(item.price) * Number(item.quantity));
          }
        });
      }
    });
    
    return totalRevenue;
  } catch (error) {
    console.error('Error fetching total revenue:', error);
    return 0;
  }
};

// Function to fetch recent activities
const fetchRecentActivities = async (): Promise<RecentActivity[]> => {
  try {
    const activities: RecentActivity[] = [];
    
    try {
      // Fetch recent user registrations - without using compound queries that require indexes
      const usersRef = collection(db, 'users');
      const recentUsersQuery = query(usersRef, limit(5));
      const recentUsers = await getDocs(recentUsersQuery);
      
      const customerUsers = recentUsers.docs
        .filter(doc => doc.data().role === 'customer')
        .slice(0, 2);
      
      customerUsers.forEach(doc => {
        const userData = doc.data();
        activities.push({
          type: 'user_registered',
          data: userData,
          timestamp: convertTimestamp(userData.createdAt),
        });
      });
      
      // Fetch recent ahavexer onboardings
      const ahavexerUsers = recentUsers.docs
        .filter(doc => doc.data().role === 'ahavexer')
        .slice(0, 2);
      
      ahavexerUsers.forEach(doc => {
        const ahavexerData = doc.data();
        activities.push({
          type: 'ahavexer_onboarded',
          data: ahavexerData,
          timestamp: convertTimestamp(ahavexerData.createdAt),
        });
      });
    } catch (error) {
      console.error('Error fetching user activities:', error);
    }
    
    try {
      // Fetch recent orders
      const ordersRef = collection(db, 'orders');
      const recentOrdersQuery = query(ordersRef, limit(2));
      const recentOrders = await getDocs(recentOrdersQuery);
      
      recentOrders.forEach(doc => {
        const orderData = doc.data();
        
        // Calculate total price for this order
        let orderTotal = 0;
        if (orderData.total && typeof orderData.total === 'number') {
          orderTotal = orderData.total;
        } else if (orderData.items && Array.isArray(orderData.items)) {
          orderData.items.forEach((item: any) => {
            if (item && item.price && item.quantity) {
              orderTotal += (Number(item.price) * Number(item.quantity));
            }
          });
        }
        
        // Add order total to the data
        const enrichedOrderData = {
          ...orderData,
          id: doc.id,
          orderTotal
        };
        
        activities.push({
          type: 'order_completed',
          data: enrichedOrderData,
          timestamp: convertTimestamp(orderData.createdAt),
        });
      });
    } catch (error) {
      console.error('Error fetching order activities:', error);
    }
    
    // Sort all activities by timestamp (newest first)
    return activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).slice(0, 3);
  } catch (error) {
    console.error('Error fetching recent activities:', error);
    return [];
  }
};

// Helper function to convert Firebase timestamp to Date
const convertTimestamp = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  
  try {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000);
    }
    
    // Handle ISO string format
    if (typeof timestamp === 'string') {
      return new Date(timestamp);
    }
    
    // Handle numeric timestamps
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    
    return new Date();
  } catch (error) {
    console.error('Error converting timestamp:', error);
    return new Date();
  }
};

// Function to fetch system health data (this would typically come from a real monitoring system)
const fetchSystemHealth = async (): Promise<SystemHealth> => {
  // In a real application, this data would come from actual system monitoring
  // For now, we'll return simulated values
  return {
    serverUptime: 99.9,
    databasePerformance: 94.2,
    apiResponseTime: 82.7,
    storageCapacity: 63.5
  };
};
